import auth from '@udacity/ureact-hoth';

import { rawDurationFilters } from '~/features/catalog/catalog-filter-data';
import { getUriFilterValueByName } from '~/features/catalog/utils/get-uri-filter-value-by-name';
import { publicEnv } from '~/features/environment/public';
import { queryUdacityService } from '~/utils/query-udacity-service';
import { sleep } from '~/utils/sleep';

import { CatalogSearchResults, catalogSearchResultsSchema } from '../models/catalog-search-result';
import { Filters, SortName, UriFilter } from '../models/filter';

/**
 * Uses the Unified Catalog API to search for catalog items based on the selected filters.
 * ref: https://github.com/udacity/unified-catalog/blob/main/docs/usage.md
 *
 * Filtering:
 *  All filters are ANDed.
 */

export async function getCatalogSearchResults({
  selectedFilters,
  selectedSort,
  page,
  schoolUriFilters,
  skillUriFilters,
  searchValue = '',
  keysToFilterBy,
  companySlug,
  shouldFetchEnrolledOnly,
}: {
  selectedFilters: Filters;
  selectedSort: SortName;
  page: number;
  schoolUriFilters: UriFilter[];
  skillUriFilters: UriFilter[];
  searchValue?: string;
  keysToFilterBy?: string[];
  companySlug?: string;
  shouldFetchEnrolledOnly?: boolean;
}): Promise<CatalogSearchResults> {
  // Add artificial delay so loading state looks less janky
  await sleep(500);

  const jwt = auth.getJWT();

  const keys = keysToFilterBy ? keysToFilterBy : [];

  const enrolledOnly = Boolean(shouldFetchEnrolledOnly);

  let isFreeSelected = false;
  let isPaidSelected = false;
  let isEnrollNowSelected = false;
  let isRequestAccessSelected = false;
  let maintainedTranslations: string[] | undefined = undefined;
  const skills: string[] = [];
  const schools: string[] = [];
  const semanticTypes: string[] = [];

  for (const [filterType, filterValues] of Object.entries(selectedFilters)) {
    if (!filterValues) continue;
    for (const filterValue of filterValues) {
      if (filterType === 'price') {
        if (filterValue === 'free') {
          isFreeSelected = true;
        } else if (filterValue === 'paid') {
          isPaidSelected = true;
        }
      }

      if (filterType === 'availability') {
        if (filterValue === 'Enroll Now') {
          isEnrollNowSelected = true;
        } else if (filterValue === 'Request Access') {
          isRequestAccessSelected = true;
        }
      }

      if (filterType === 'skill') {
        const skill = getUriFilterValueByName(skillUriFilters, filterValue);
        if (skill) {
          skills.push(skill);
        }
      }

      if (filterType === 'school') {
        const school = getUriFilterValueByName(schoolUriFilters, filterValue);
        if (school) {
          schools.push(school);
        }
      }

      if (filterType === 'type') {
        if (filterValue == 'course') {
          semanticTypes.push('Course');
          semanticTypes.push('Part');
        } else if (filterValue === 'masters') {
          semanticTypes.push('Academic_Degree');
        } else if (filterValue === 'Learning_Plan') {
          semanticTypes.push('Learning_Plan');
        } else {
          semanticTypes.push('Degree');
        }
      }

      if (filterType === 'language' && filterValue) {
        maintainedTranslations = filterValue === 'ja' ? ['ja'] : [];
      }
    }
  }

  const freeToPublic = isTriboolFilterSelected(isFreeSelected, isPaidSelected);
  const autoEnrollable = isTriboolFilterSelected(isEnrollNowSelected, isRequestAccessSelected);

  const rawDurations: string[] = [];
  // Get the corresponding time ranges from the label and send _those_ to the API.
  selectedFilters.rawDuration?.forEach((label) => {
    const filterValue = rawDurationFilters.find((preset) => preset.label === label)?.value;
    if (filterValue) {
      rawDurations.push(filterValue);
    }
  });

  const data = {
    searchText: searchValue,
    sortBy: getApiSortBy(selectedSort),
    page: page,
    pageSize: 24,
    keys,
    freeToPublic,
    skills,
    schools,
    durations: selectedFilters.duration,
    rawDurations,
    difficulties: selectedFilters.difficulty,
    semanticTypes,
    companySlug,
    allowEnterpriseLPs: Boolean(companySlug),
    sortLPs: Boolean(companySlug),
    enrolledOnly,
    autoEnrollable,
    maintainedTranslations,
  };

  return await queryUdacityService({
    url: publicEnv.NEXT_PUBLIC_UNIFIED_CATALOG_URL,
    zodSchema: catalogSearchResultsSchema,
    body: JSON.stringify(data),
    authorization: jwt ? `Bearer ${jwt}` : undefined,
  });
}

function getApiSortBy(selectedSort: SortName) {
  const apiSortBy = {
    relevance: 'relevance',
    'most-popular': 'enrollment',
    'highest-rated': 'avgRating',
    'recently-updated': 'updated',
  } as const;

  return apiSortBy[selectedSort];
}

function isTriboolFilterSelected(isTrueSelected: boolean, isFalseSelected: boolean): boolean | undefined {
  // If both options are unchecked or checked, unset this filter.
  if ((!isTrueSelected && !isFalseSelected) || (isTrueSelected && isFalseSelected)) {
    return undefined;
  }
  if (isFalseSelected) {
    return false;
  }
  if (isTrueSelected) {
    return true;
  }
}
